import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/card';

import { ColumnChart, fillDateCategories, MAIN_CHART_HEIGHT } from '@/features/dashboard-charts';
import { getMetricLabel, getTransportChartTitle } from '@/features/dashboard-filters';
import { getExportOptions } from '@/features/chart-export';

import { PageHeader } from '@/shared/ui';
import { MANUAL_NODES } from '@/shared/config/manual';

import type { useTransportOverview } from '../lib/use-transport-overview';
import { TransportStats, ChartSection, TopListsGrid } from './components';

interface TransportOverviewPageProps {
  /** Owned by `TransportOverviewWithFilters`, which also publishes it to the header. */
  transport: ReturnType<typeof useTransportOverview>;
}

export function TransportOverviewPage({ transport }: TransportOverviewPageProps) {
  const { t } = useTranslation();
  const { data, transportChart, formattedStats, isLoading, isFetching, error, urlState } = transport;

  const exportOptions = useMemo(() => getExportOptions('transport-overview'), []);
  const chart = useMemo(
    () =>
      transportChart &&
      fillDateCategories(
        {
          ...transportChart,
          title: getTransportChartTitle(t, urlState.metric, urlState.group),
          metricLabel: getMetricLabel(t, urlState.metric)
        },
        urlState
      ),
    [transportChart, t, urlState]
  );

  if (error) {
    return (
      <div>
        <Card className="p-4 md:p-6">
          <p className="text-destructive">Error loading transport overview data</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <PageHeader
        title={t('report.transport.overview.title', 'Transport Overview')}
        helpTarget={MANUAL_NODES.TRANSPORT_OVERVIEW}>
        <TransportStats stats={formattedStats} isLoading={isLoading || isFetching} />
      </PageHeader>

      <ChartSection>
        <ColumnChart
          data={chart}
          height={MAIN_CHART_HEIGHT}
          loading={isLoading || isFetching}
          showExport
          exportOptions={exportOptions}
        />
      </ChartSection>

      <TopListsGrid
        topTransporters={data?.topTransporters}
        topTransportUsers={data?.topTransportUsers}
        isLoading={isLoading || isFetching}
      />
    </div>
  );
}
